import React, { useState, useEffect, useRef, useMemo } from 'react';
import Lightbox from '../Lightbox/Lightbox';
import { preventImageProtection } from '../../utils/imageProtection';
import { useTranslation } from '../../hooks/useTranslation';
import { encUrl } from '../../utils/encUrl';
import { photoPath } from '../../utils/collections';
import EncryptedImage from '../EncryptedImage/EncryptedImage';
import styles from './ImageGallery.module.css';

// Column count for the current screen width
const getColumnCount = () => {
  const width = window.innerWidth;
  if (width <= 500) return 1;
  if (width <= 900) return 2;
  if (width <= 1200) return 3;
  return 4;
};

const ImageGallery = ({ images }) => {
  const { t } = useTranslation();
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  // Read synchronously on mount: starting at 4 and correcting in an effect flashed a
  // 4-column layout on phones each time the gallery re-mounted (filter switch)
  const [columnCount, setColumnCount] = useState(getColumnCount);
  const containerRef = useRef(null);
  const imageHeights = useRef({});

  useEffect(() => {
    const updateColumnCount = () => setColumnCount(getColumnCount());
    window.addEventListener('resize', updateColumnCount);
    return () => window.removeEventListener('resize', updateColumnCount);
  }, []);

  // Distribute images across columns during render, so the first paint is already final
  const columns = useMemo(() => {
    const newColumns = Array.from({ length: columnCount }, () => []);
    const columnHeights = Array(columnCount).fill(0);

    // Distribute images to shortest column
    (images || []).forEach((image, index) => {
      const shortestColumn = columnHeights.indexOf(Math.min(...columnHeights));
      newColumns[shortestColumn].push({ ...image, originalIndex: index });
      // Use pre-computed ratio from photos.json, fall back to runtime-measured or square
      const estimatedHeight = image.ratio || imageHeights.current[image.id] || 1;
      columnHeights[shortestColumn] += estimatedHeight;
    });

    return newColumns;
  }, [images, columnCount]);

  // Track image heights for better distribution (works with both img and EncryptedImage)
  const handleImageLoad = (imageId, event) => {
    const { naturalWidth, naturalHeight } = event.target;
    if (naturalWidth) imageHeights.current[imageId] = naturalHeight / naturalWidth;
  };

  const handleImageClick = (originalIndex) => {
    setSelectedImageIndex(originalIndex);
    setIsLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedImageIndex(null);
  };

  const handlePrevious = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setSelectedImageIndex((prev) =>
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  if (!images || images.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>{t('gallery.emptyState')}</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.gallery} ref={containerRef}>
        {columns.map((column, colIndex) => (
          <div key={colIndex} className={styles.column}>
            {column.map((image) => (
              <div key={image.id} className={styles.imageCard}>
                <div
                  className={styles.imageWrapper}
                  onClick={() => handleImageClick(image.originalIndex)}
                >
                  <EncryptedImage
                    src={encUrl(photoPath(image))}
                    alt={image.alt}
                    className={styles.image}
                    ratio={image.ratio}
                    onLoad={(e) => handleImageLoad(image.id, e)}
                    onContextMenu={preventImageProtection}
                    onDragStart={preventImageProtection}
                  />
                  {image.title && (
                    <div className={styles.imageOverlay}>
                      <span className={styles.imageTitle}>{image.title}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {isLightboxOpen && selectedImageIndex !== null && (
        <Lightbox
          images={images}
          currentIndex={selectedImageIndex}
          onClose={handleCloseLightbox}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      )}
    </>
  );
};

export default ImageGallery;
